/**
 * @fileoverview ButtonGroup - Group related buttons together
 *
 * Purpose: Visually group related buttons with connected borders
 * Dependencies: Radix UI Slot, class-variance-authority, Separator
 * Used by: Toolbars, filter controls, pagination, segmented controls
 *
 * Components exported:
 * - ButtonGroup: Container for grouped buttons
 * - ButtonGroupText: Text label within button group
 * - ButtonGroupSeparator: Visual separator between groups
 *
 * Features:
 * - Horizontal or vertical orientation
 * - Connected borders (buttons appear as one unit)
 * - Focus management (focused button appears on top)
 * - Accessible (proper roles and ARIA)
 * - Works with Button component
 *
 * @example
 * <ButtonGroup>
 *   <Button variant="outline">Left</Button>
 *   <Button variant="outline">Center</Button>
 *   <Button variant="outline">Right</Button>
 * </ButtonGroup>
 *
 * @example
 * // With text label and separator
 * <ButtonGroup>
 *   <ButtonGroupText>View:</ButtonGroupText>
 *   <ButtonGroupSeparator />
 *   <Button variant="outline">List</Button>
 *   <Button variant="outline">Grid</Button>
 * </ButtonGroup>
 *
 * @example
 * // Vertical orientation
 * <ButtonGroup orientation="vertical">
 *   <Button variant="outline">Top</Button>
 *   <Button variant="outline">Middle</Button>
 *   <Button variant="outline">Bottom</Button>
 * </ButtonGroup>
 */

import { Slot } from "@radix-ui/react-slot";
import { type VariantProps } from "class-variance-authority";

import { cn } from "@/utils/misc";
import { Separator } from "./separator";
import { buttonGroupVariants } from "./button-group.variants";

function ButtonGroup({
  className,
  orientation,
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof buttonGroupVariants>) {
  return (
    <div
      role="group"
      data-slot="button-group"
      data-orientation={orientation}
      className={cn(buttonGroupVariants({ orientation }), className)}
      {...props}
    />
  );
}

function ButtonGroupText({
  className,
  asChild = false,
  ...props
}: React.ComponentProps<"div"> & {
  asChild?: boolean;
}) {
  const Comp = asChild ? Slot : "div";

  return (
    <Comp
      className={cn(
        "shadow-xs flex items-center gap-2 rounded-md border bg-muted px-4 text-sm font-medium [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none",
        className,
      )}
      {...props}
    />
  );
}

function ButtonGroupSeparator({
  className,
  orientation = "vertical",
  ...props
}: React.ComponentProps<typeof Separator>) {
  return (
    <Separator
      data-slot="button-group-separator"
      orientation={orientation}
      className={cn(
        "relative !m-0 self-stretch bg-input data-[orientation=vertical]:h-auto",
        className,
      )}
      {...props}
    />
  );
}

export { ButtonGroup, ButtonGroupSeparator, ButtonGroupText };
